import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogTrigger, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Star, MessageSquare } from "lucide-react";
import { toast } from "sonner";

export function RatingButton({ variant = "default", externalOpen, onExternalOpenChange, hideTrigger = false }) {
  const [open, setOpen] = useState(false);
  const [stars, setStars] = useState(5);
  const [hover, setHover] = useState(0);
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Allow parent to control open state
  const dialogOpen = externalOpen !== undefined ? externalOpen : open;
  const setDialogOpen = onExternalOpenChange || setOpen;

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post("/ratings", { stars, note });
      toast.success("شكراً لتقييمك ❤️ ملاحظتك وصلت للإدارة");
      setOpen(false);
      setNote("");
      setStars(5);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const isHero = variant === "hero";

  return (
    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
      {!hideTrigger && (
        <DialogTrigger asChild>
          {isHero ? (
            <Button size="lg" className="gap-2 bg-amber-400 hover:bg-amber-500 text-amber-950 border-0 shadow-lg" data-testid="rate-app-btn">
              <Star className="size-5 fill-amber-950" />
              قيّم التطبيق
            </Button>
          ) : (
            <Button variant="outline" size="sm" className="gap-2 border-amber-400 text-amber-700 hover:bg-amber-50" data-testid="rate-app-btn">
              <Star className="size-4 fill-amber-500 text-amber-500" />
              قيّم التطبيق
            </Button>
          )}
        </DialogTrigger>
      )}
      <DialogContent dir="rtl" className="max-w-md">
        <DialogTitle className="flex items-center gap-2">
          <MessageSquare className="size-5" /> قيّم تطبيق هود
        </DialogTitle>
        <div className="space-y-4">
          <div>
            <div className="text-sm font-semibold mb-2">كم نجمة تستحق؟</div>
            <div className="flex gap-1" onMouseLeave={() => setHover(0)} data-testid="star-picker">
              {[1, 2, 3, 4, 5].map(i => (
                <button
                  key={i}
                  type="button"
                  onMouseEnter={() => setHover(i)}
                  onClick={() => setStars(i)}
                  className="p-1 transition-transform hover:scale-110"
                  data-testid={`star-${i}`}
                  aria-label={`${i} نجوم`}
                >
                  <Star
                    className={`size-9 ${(hover || stars) >= i ? "fill-amber-500 text-amber-500" : "text-muted-foreground/30"} transition-colors`}
                  />
                </button>
              ))}
            </div>
            <div className="text-sm text-muted-foreground mt-1">اخترت: {stars} / 5</div>
          </div>

          <div>
            <div className="text-sm font-semibold mb-2">ملاحظتك للإدارة (اختياري)</div>
            <Textarea
              value={note}
              onChange={e => setNote(e.target.value)}
              placeholder="ما الذي أعجبك؟ ما الذي تريد تحسينه؟"
              rows={4}
              data-testid="rating-note"
              maxLength={1000}
            />
            <div className="text-xs text-muted-foreground mt-1">
              💬 ملاحظتك ستذهب مباشرة للأدمن الرئيسي فقط ولن يراها أحد غيره
            </div>
          </div>

          <Button onClick={submit} disabled={submitting} className="w-full bg-amber-500 hover:bg-amber-600 text-white" data-testid="rating-submit-btn">
            {submitting ? "..." : "إرسال التقييم"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
